import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, ArrowRightCircle, UserPlus, Users } from "lucide-react";
import { listContacts, convertLead } from "../lib/api";

const INPUT_CLS =
    "bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]";

// Pull a sensible prefill value out of lead cells by matching column name.
// First case-insensitive substring match wins.
function pickCell(lead, columns, ...needles) {
    if (!lead || !columns) return "";
    const cells = lead.cells || {};
    for (const needle of needles) {
        const col = columns.find((c) =>
            (c.name || "").toLowerCase().includes(needle.toLowerCase()),
        );
        if (col && cells[col.id]) return String(cells[col.id]);
    }
    return "";
}

export default function ConvertLeadDialog({
    open,
    onOpenChange,
    lead,
    columns,
    onConverted,
}) {
    const [contacts, setContacts] = useState([]);
    const [contactMode, setContactMode] = useState("new"); // 'new' | 'existing' | 'none'
    const [contactId, setContactId] = useState("");

    const [name, setName] = useState("");
    const [location, setLocation] = useState("");
    const [jobId, setJobId] = useState("");
    const [contactName, setContactName] = useState("");
    const [contactCompany, setContactCompany] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Prefill when the dialog opens on a new lead.
    useEffect(() => {
        if (!open || !lead) return;
        const n = pickCell(lead, columns, "name", "customer", "client");
        const addr = pickCell(lead, columns, "address", "location", "site");
        const company = pickCell(lead, columns, "company", "organisation", "organization");
        const email = pickCell(lead, columns, "email");
        const phone = pickCell(lead, columns, "phone", "cell", "mobile");
        setName(n);
        setLocation(addr);
        setJobId(`PRJ-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`);
        setContactName(n);
        setContactCompany(company);
        setContactEmail(email);
        setContactPhone(phone);
        setContactMode("new");
        setContactId("");
    }, [open, lead, columns]);

    // Lazy-load contacts the first time the user picks "existing".
    useEffect(() => {
        if (contactMode !== "existing" || contacts.length > 0) return;
        (async () => {
            try {
                const data = await listContacts();
                setContacts(data);
                if (data.length) setContactId(data[0].id);
            } catch {
                toast.error("Couldn't load contacts");
            }
        })();
    }, [contactMode, contacts.length]);

    const canSubmit = useMemo(() => {
        if (!name.trim() || !location.trim() || !jobId.trim()) return false;
        if (contactMode === "existing" && !contactId) return false;
        if (contactMode === "new" && !contactName.trim()) return false;
        return true;
    }, [name, location, jobId, contactMode, contactId, contactName]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit || !lead) return;
        setSubmitting(true);
        try {
            const payload = {
                name: name.trim(),
                location: location.trim(),
                job_id: jobId.trim(),
            };
            if (contactMode === "existing") {
                payload.contact_id = contactId;
            } else if (contactMode === "new") {
                payload.contact = {
                    name: contactName.trim(),
                    company: contactCompany.trim(),
                    email: contactEmail.trim(),
                    cell_phone: contactPhone.trim(),
                    address: location.trim(),
                };
            }
            const res = await convertLead(lead.id, payload);
            toast.success(
                res.already_converted
                    ? "Opening linked project"
                    : "Lead signed · project created",
            );
            onConverted?.(res.project);
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Couldn't convert this lead.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[520px] max-h-[88vh] overflow-hidden p-4 sm:p-6"
                data-testid="convert-lead-dialog"
            >
                <div className="min-w-0 flex flex-col gap-4 max-h-[calc(88vh-2rem)] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <ArrowRightCircle className="w-5 h-5 text-[#00A3FF]" />
                            Sign lead &amp; create project
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            We'll mint a new Project (and link a Contact) from this lead.
                            You can edit every field before saving.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Customer / project name
                            </Label>
                            <Input
                                data-testid="convert-project-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={INPUT_CLS}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Site address
                            </Label>
                            <Input
                                data-testid="convert-project-location"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                className={INPUT_CLS}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Project ID
                            </Label>
                            <Input
                                data-testid="convert-project-job-id"
                                value={jobId}
                                onChange={(e) => setJobId(e.target.value)}
                                className={INPUT_CLS}
                                required
                            />
                        </div>

                        <div className="pt-3 border-t border-[#2A3655] space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Contact
                            </Label>
                            <div
                                role="radiogroup"
                                className="grid grid-cols-3 gap-1 p-1 bg-[#0A1128] border border-[#2A3655] rounded-md"
                            >
                                {[
                                    { key: "new", label: "Create new", icon: UserPlus },
                                    { key: "existing", label: "Pick existing", icon: Users },
                                    { key: "none", label: "No contact", icon: null },
                                ].map((m) => {
                                    const Icon = m.icon;
                                    const active = contactMode === m.key;
                                    return (
                                        <button
                                            type="button"
                                            key={m.key}
                                            onClick={() => setContactMode(m.key)}
                                            data-testid={`convert-contact-mode-${m.key}`}
                                            className={`flex items-center justify-center gap-1.5 py-2 rounded-sm text-xs font-semibold transition-colors ${
                                                active
                                                    ? "bg-[#007AFF] text-white"
                                                    : "text-slate-300 hover:text-white hover:bg-[#1C253C]"
                                            }`}
                                        >
                                            {Icon && <Icon className="w-3.5 h-3.5" />}
                                            {m.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {contactMode === "new" && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                                    <Input
                                        data-testid="convert-contact-name"
                                        placeholder="Contact name"
                                        value={contactName}
                                        onChange={(e) => setContactName(e.target.value)}
                                        className={INPUT_CLS}
                                    />
                                    <Input
                                        data-testid="convert-contact-company"
                                        placeholder="Company"
                                        value={contactCompany}
                                        onChange={(e) => setContactCompany(e.target.value)}
                                        className={INPUT_CLS}
                                    />
                                    <Input
                                        data-testid="convert-contact-email"
                                        placeholder="Email"
                                        type="email"
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                        className={INPUT_CLS}
                                    />
                                    <Input
                                        data-testid="convert-contact-phone"
                                        placeholder="Phone"
                                        value={contactPhone}
                                        onChange={(e) => setContactPhone(e.target.value)}
                                        className={INPUT_CLS}
                                    />
                                </div>
                            )}

                            {contactMode === "existing" && (
                                <div className="pt-1">
                                    {contacts.length === 0 ? (
                                        <div className="text-xs text-slate-500 px-1 py-2">
                                            No contacts in your address book yet.
                                        </div>
                                    ) : (
                                        <select
                                            data-testid="convert-contact-select"
                                            value={contactId}
                                            onChange={(e) => setContactId(e.target.value)}
                                            className="w-full bg-[#0A1128] border border-[#2A3655] rounded-sm text-sm text-white px-2.5 py-2 focus:outline-none focus:border-[#00A3FF]"
                                        >
                                            {contacts.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                    {c.company ? ` · ${c.company}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}
                        </div>

                        <DialogFooter className="gap-2 sm:gap-2 pt-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                data-testid="btn-cancel-convert"
                                className="text-slate-300 hover:text-white hover:bg-[#1C253C]"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!canSubmit || submitting}
                                data-testid="btn-submit-convert"
                                className="bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold disabled:opacity-40 disabled:bg-[#2A3655] disabled:text-slate-400"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Converting...
                                    </>
                                ) : (
                                    <>
                                        <ArrowRightCircle className="w-4 h-4 mr-2" />
                                        Sign &amp; create project
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}
